import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ConfirmDialog } from "./ConfirmDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Phone, Users, StickyNote, Mail, Plus, Trash2 } from "lucide-react";
import { formatDateTime } from "@/lib/format";

const inputCls = "rounded-xl h-11";
const selectCls =
  "rounded-xl h-11 w-full px-3 text-sm bg-white border border-gray-300 text-brand-navy focus:border-brand-orange focus:outline-none";

const TYPES = [
  { value: "call", label: "Call", Icon: Phone, cls: "bg-blue-100 text-blue-800" },
  { value: "meeting", label: "Meeting", Icon: Users, cls: "bg-violet-100 text-violet-800" },
  { value: "note", label: "Note", Icon: StickyNote, cls: "bg-amber-100 text-amber-800" },
  { value: "email", label: "Email", Icon: Mail, cls: "bg-emerald-100 text-emerald-800" },
];

const typeMeta = (v) => TYPES.find((t) => t.value === v) || TYPES[2];

/**
 * ActivityTimeline: timeline of activities scoped to a deal (and its contact).
 * Props: dealId, contactId (optional), title (optional).
 */
export function ActivityTimeline({ dealId, contactId, title = "Activity Timeline" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [showLog, setShowLog] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const fetch = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    try {
      const params = { deal_id: dealId };
      if (filter) params.type = filter;
      const { data } = await api.get("/admin/activities", { params });
      setItems(data.items || []);
    } catch {
      toast.error("Failed to load activities");
    } finally {
      setLoading(false);
    }
  }, [dealId, filter]);

  useEffect(() => { fetch(); }, [fetch]);

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/admin/activities/${toDelete.id}`);
      setItems((arr) => arr.filter((x) => x.id !== toDelete.id));
      toast.success("Activity deleted");
    } catch {
      toast.error("Failed to delete");
    } finally {
      setToDelete(null);
    }
  };

  return (
    <section className="bg-white border border-brand-navy/10 rounded-2xl p-6" data-testid="activity-timeline">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <StickyNote size={16} className="text-brand-orange" />
          <h2 className="text-sm font-semibold uppercase tracking-[0.25em] text-brand-navy">{title}</h2>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-full text-xs font-medium border border-brand-navy/15 bg-white text-brand-navy px-3 py-1.5 focus:border-brand-orange focus:outline-none" data-testid="activity-filter">
            <option value="">All types</option>
            {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <button
            onClick={() => setShowLog(true)}
            className="inline-flex items-center gap-1 bg-brand-orange text-white px-3 py-1.5 text-xs font-semibold rounded-full hover:bg-brand-orange-hover transition-colors"
            data-testid="activity-log-button"
          >
            <Plus size={12} /> Log activity
          </button>
        </div>
      </div>

      {loading && <p className="text-sm text-brand-slate/60">Loading…</p>}
      {!loading && items.length === 0 && (
        <p className="text-sm text-brand-slate/60 py-6 text-center" data-testid="activity-empty">
          No activities yet. Log a call, meeting, note, or email to build history.
        </p>
      )}

      <ol className="space-y-4">
        {items.map((it) => {
          const meta = typeMeta(it.type);
          const Icon = meta.Icon;
          return (
            <li key={it.id} className="flex gap-3 group" data-testid={`activity-item-${it.id}`}>
              <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${meta.cls}`}>
                <Icon size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-brand-navy">
                    {it.subject || meta.label}
                  </p>
                  <button
                    onClick={() => setToDelete(it)}
                    className="text-brand-slate/40 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Delete"
                    data-testid={`activity-delete-${it.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
                {it.body && (
                  <p className="mt-1 text-sm text-brand-slate whitespace-pre-wrap">{it.body}</p>
                )}
                <p className="mt-1 text-[11px] text-brand-slate/50">
                  {meta.label} · {it.created_by_name || "—"} · {formatDateTime(it.occurred_at)}
                </p>
              </div>
            </li>
          );
        })}
      </ol>

      <LogActivityDialog
        open={showLog}
        onOpenChange={setShowLog}
        dealId={dealId}
        contactId={contactId}
        onCreated={(a) => setItems((arr) => [a, ...arr])}
      />

      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        title="Delete this activity?"
        description="This entry will be removed from the timeline."
        confirmLabel="Delete"
        destructive
        onConfirm={doDelete}
      />
    </section>
  );
}

function LogActivityDialog({ open, onOpenChange, dealId, contactId, onCreated }) {
  const [type, setType] = useState("note");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [occurredAt, setOccurredAt] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => { setType("note"); setSubject(""); setBody(""); setOccurredAt(""); setSaving(false); };
  const close = (o) => { if (!o) reset(); onOpenChange(o); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { type, deal_id: dealId };
      if (contactId) payload.contact_id = contactId;
      if (subject.trim()) payload.subject = subject.trim();
      if (body.trim()) payload.body = body.trim();
      if (occurredAt) payload.occurred_at = new Date(occurredAt).toISOString();
      const { data } = await api.post("/admin/activities", payload);
      toast.success("Activity logged");
      onCreated?.(data);
      close(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to log");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-lg" data-testid="log-activity-dialog">
        <DialogHeader>
          <DialogTitle className="text-brand-navy">Log activity</DialogTitle>
          <DialogDescription>Record a call, meeting, note, or email on this customer.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex flex-col gap-1.5">
            <Label className="text-sm text-brand-navy">Type</Label>
            <select value={type} onChange={(e) => setType(e.target.value)} className={selectCls} data-testid="activity-input-type">
              {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-sm text-brand-navy">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Short summary" className={inputCls} data-testid="activity-input-subject" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-sm text-brand-navy">Details</Label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} className="rounded-xl w-full px-3 py-2 text-sm bg-white border border-gray-300 text-brand-navy focus:border-brand-orange focus:outline-none" data-testid="activity-input-body" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-sm text-brand-navy">Occurred at</Label>
            <Input type="datetime-local" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} className={inputCls} data-testid="activity-input-occurred" />
          </div>
          <DialogFooter>
            <button type="button" onClick={() => close(false)} className="inline-flex items-center gap-2 border border-brand-navy/20 px-5 py-2.5 text-xs font-semibold text-brand-navy hover:bg-brand-cream rounded-full transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-brand-orange text-white px-6 py-2.5 text-xs font-semibold hover:bg-brand-orange-hover rounded-full transition-colors disabled:opacity-60" data-testid="activity-submit">
              {saving ? "Saving…" : "Log activity"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
